import {useCallback, useEffect, useState} from 'react'

type Validator = (data: unknown) => boolean

type Params<Value> = {
  defaultValue: Value
  entity: string
  validator: Validator
}

type Result<Value> = [value: Value, setValue: (valueOrSetter: Value | ((value: Value) => Value)) => Value]

export const load = <Value>(entity: string, defaultValue: Value, validator: Validator) => {
  const json = window.localStorage.getItem(entity)

  if (!json) {
    return defaultValue
  }

  const loadedData = JSON.parse(json)
  const isValidData = validator(loadedData)
  return isValidData ? (loadedData as Value) : defaultValue
}

export const save = <Value>(entity: string, value: Value) => {
  window.localStorage.setItem(entity, JSON.stringify(value))
}

export const useLocalStorageState = <Value>({defaultValue, entity, validator}: Params<Value>): Result<Value> => {
  const [value, setValue] = useState<Value>(load(entity, defaultValue, validator))

  const setValueAndNotify = useCallback(
    (valueOrSetter: Value | ((value: Value) => Value)) => {
      const value = load<Value>(entity, defaultValue, validator)
      const newValue = valueOrSetter instanceof Function ? valueOrSetter(value) : valueOrSetter
      save(entity, newValue)
      window.postMessage({[entity]: newValue, type: `${entity}:update`})
      return newValue
    },
    [defaultValue, entity, validator]
  )

  useEffect(() => {
    const listener = (event: any) => {
      if (event.data.type !== `${entity}:update`) {
        return
      }

      setValue(event.data[entity])
    }

    window.addEventListener('message', listener)

    return () => {
      window.removeEventListener('message', listener)
    }
  }, [entity])

  return [value, setValueAndNotify]
}
